import React, { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { DialogsAndToastsContext } from '../../../contexts/dialogs-and-toasts';
import { ROLES, SxStyle } from '../../../types';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import PollPlayerModal from './components/modals/poll-player-modal';
import PollQuizModal from './components/modals/poll-quiz-modal';
import PollDeleteModal from './components/modals/poll-delete-modal';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Poll, PollStatus, PollType } from './types';
import PollCreatedModal from './components/modals/poll-created-modal';
import PollListHeader from './components/poll-list-header';
import svStrings from '../../../sv-strings';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useQueryClient } from '@tanstack/react-query';
import PollFreeModal from './components/modals/poll-free-modal';
import ScenePage from '../../common/scene/scene-page';
import PollPushNotificationColumn from './components/polls-push-notification-column';
import { pollTypeString } from './constants';
import { usePollsList, PollListDataGridFormat } from './hooks/usePollsList';
import { usePollById } from './hooks/usePollById';
import { useUserAuth } from '../../../contexts/auth';

const sortOrderStatus: PollStatus[] = ['ONGOING', 'UPCOMING', 'FINISHED'];

const pollStatusString: Record<PollStatus, string> = {
  ONGOING: svStrings.polls.statusOngoing,
  UPCOMING: svStrings.polls.statusUpcoming,
  FINISHED: svStrings.polls.statusFinished,
};

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 80px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  }),
};

interface ListEditProps {
  params: GridRenderCellParams;
  onEdit: (event: React.MouseEvent, poll: PollListDataGridFormat) => void;
  onDelete: (event: React.MouseEvent, poll: PollListDataGridFormat) => void;
  isReadOnly: boolean;
}

const ListStatusHeader = () => (
  <Stack direction="row" alignItems="center" spacing={2}>
    <Box sx={{ fontWeight: 500 }}>{svStrings.polls.fieldNameStatus}</Box>
    <Tooltip
      placement="top"
      arrow
      title={
        <>
          <div>{svStrings.polls.listToolTipOngoing}</div>
          <div>{svStrings.polls.listToolTipUpcoming}</div>
          <div>{svStrings.polls.listToolTipOFinished}</div>
        </>
      }
    >
      <InfoIcon fontSize="small" color="action" />
    </Tooltip>
  </Stack>
);

const ListEdit = ({ params, onEdit, onDelete, isReadOnly }: ListEditProps) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <IconButton onClick={(event) => onEdit(event, params.row)}>
      <EditIcon color={isReadOnly ? 'disabled' : 'primary'} />
    </IconButton>
    <IconButton onClick={(event) => onDelete(event, params.row)}>
      <DeleteIcon color={isReadOnly ? 'disabled' : 'error'} />
    </IconButton>
  </Stack>
);

interface Props {
  match: { params: { abbrv: string } };
}

export default function PollsList({ match }: Props) {
  const { strings } = useStringsContext();
  const { showToast, hideToast } = useContext(DialogsAndToastsContext);
  const queryClient = useQueryClient();

  const [showPollDialog, setShowPollDialog] = useState<PollType | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCreatedDialog, setShowCreatedDialog] = useState(false);
  const [selectedPoll, setSelectedPoll] = useState<Poll | null>(null);
  const [selectedPollId, setSelectedPollId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'edit' | 'delete' | null>(null);

  const { abbrv: selectedAbbrv } = match.params;

  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv, ROLES.VIEWER);

  // Use the new GraphQL-based hook for polls list
  const { polls, isLoading, hardRefresh } = usePollsList(selectedAbbrv);

  // Fetch full poll data when editing/deleting
  const { data: fetchedPoll, isLoading: isLoadingPoll } = usePollById(selectedPollId);

  // Handle fetched poll data for edit/delete actions
  useEffect(() => {
    if (fetchedPoll && pendingAction) {
      setSelectedPoll(fetchedPoll);
      if (pendingAction === 'edit') {
        setShowPollDialog(fetchedPoll.type);
      } else if (pendingAction === 'delete') {
        setShowDeleteDialog(true);
      }
      setPendingAction(null);
    }
  }, [fetchedPoll, pendingAction]);

  const handleHardRefresh = useCallback(async () => {
    showToast({ message: 'Refreshing...', variant: 'info' });
    await hardRefresh();
    hideToast();
  }, [hardRefresh, showToast, hideToast]);

  const invalidatePolls = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pollsList', selectedAbbrv] });
  }, [queryClient, selectedAbbrv]);

  const onEdit = useCallback((event: React.MouseEvent, row: PollListDataGridFormat) => {
    event.stopPropagation();
    setSelectedPollId(row.id);
    setPendingAction('edit');
  }, []);

  const onDelete = useCallback((event: React.MouseEvent, row: PollListDataGridFormat) => {
    event.stopPropagation();
    setSelectedPollId(row.id);
    setPendingAction('delete');
  }, []);

  const onDeleteEdit = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const onAdd = useCallback((type: PollType) => {
    setSelectedPoll(null);
    setShowPollDialog(type);
  }, []);

  const onRowClick = useCallback(
    (params: GridRowParams, event: React.MouseEvent) => {
      onEdit(event, params.row as PollListDataGridFormat);
    },
    [onEdit]
  );

  const onCloseEdit = useCallback(() => {
    setShowPollDialog(null);
    setSelectedPoll(null);
    setSelectedPollId(null);
  }, []);

  const onCloseDelete = useCallback(() => {
    setShowDeleteDialog(false);
    setSelectedPollId(null);
  }, []);

  const onCloseCreated = useCallback(() => {
    setShowCreatedDialog(false);
    setSelectedPoll(null);
    setSelectedPollId(null);
  }, []);

  const onEditDone = useCallback(() => {
    setShowPollDialog(null);
    setShowDeleteDialog(false);
    setSelectedPoll(null);
    setSelectedPollId(null);
    invalidatePolls();
  }, [invalidatePolls]);

  const onCreateDone = useCallback(
    (createdPoll: Poll) => {
      setShowPollDialog(null);
      setShowDeleteDialog(false);
      setSelectedPoll(createdPoll);
      setShowCreatedDialog(true);
      invalidatePolls();
    },
    [invalidatePolls]
  );

  const onDoneDelete = useCallback(() => {
    setShowPollDialog(null);
    setShowDeleteDialog(false);
    setSelectedPoll(null);
    setSelectedPollId(null);
    invalidatePolls();
  }, [invalidatePolls]);

  const columns = useMemo(
    (): GridColDef[] => [
      { field: 'name', headerName: strings.polls.fieldNameName, width: 300 },
      { field: 'createdAt', headerName: strings.polls.fieldNameCreated, width: 160 },
      { field: 'title', headerName: strings.polls.fieldNameTitle, width: 300 },
      {
        field: 'type',
        headerName: strings.polls.fieldNameType,
        width: 150,
        valueGetter: (value) => pollTypeString[value as PollType],
      },
      {
        field: 'result',
        headerName: strings.polls.fieldNameVotes,
        width: 100,
      },
      {
        field: 'mostVotedText',
        headerName: strings.polls.fieldNameMostVoted,
        width: 200,
      },
      {
        field: 'status',
        headerName: strings.polls.fieldNameStatus,
        minWidth: 100,
        flex: 1,
        sortComparator: (a, b) => sortOrderStatus.indexOf(a) - sortOrderStatus.indexOf(b),
        renderHeader: () => <ListStatusHeader />,
        valueGetter: (value) => pollStatusString[value as PollStatus],
      },
      {
        field: 'push-notification',
        headerName: strings.polls.pushNotificationColumn,
        width: 300,
        renderCell: (params: GridRenderCellParams) => (
          <PollPushNotificationColumn poll={params.row} selectedAbbrv={selectedAbbrv} />
        ),
        align: 'center',
      },
      {
        field: 'edit',
        headerName: '',
        minWidth: 100,
        maxWidth: 100,
        align: 'right',
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ListEdit params={params} onEdit={onEdit} onDelete={onDelete} isReadOnly={isReadOnly} />
        ),
      },
    ],
    [onDelete, onEdit, strings, selectedAbbrv]
  );

  return (
    <>
      <ScenePage title={strings.appBar.polls}>
        <PollListHeader onAdd={onAdd} isReadOnly={isReadOnly} />
        <Paper elevation={2} sx={styles.container}>
          <DataGrid
            sx={styles.dataGrid}
            rows={polls ?? []}
            columns={columns}
            autoPageSize
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            onRowClick={onRowClick}
            loading={isLoading}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: 'createdAt',
                    sort: 'desc',
                  },
                ],
              },
            }}
          />
        </Paper>
      </ScenePage>
      {(showPollDialog === 'playerofthematch' ||
        showPollDialog === 'playerofthemonth' ||
        showPollDialog === 'coachofthemonth') &&
        selectedAbbrv && (
          <PollPlayerModal
            open={!!showPollDialog}
            onClose={onCloseEdit}
            onCreateDone={onCreateDone}
            onEditDone={onEditDone}
            onDelete={onDeleteEdit}
            selectedPoll={selectedPoll}
            selectedAbbrv={selectedAbbrv}
            pollType={showPollDialog}
          />
        )}
      {showPollDialog === 'quiz' && selectedAbbrv && (
        <PollQuizModal
          open={!!showPollDialog}
          onClose={onCloseEdit}
          onCreateDone={onCreateDone}
          onEditDone={onEditDone}
          onDelete={onDeleteEdit}
          selectedPoll={selectedPoll}
          selectedAbbrv={selectedAbbrv}
        />
      )}
      {showPollDialog === 'free' && selectedAbbrv && (
        <PollFreeModal
          open={!!showPollDialog}
          onClose={onCloseEdit}
          onCreateDone={onCreateDone}
          onEditDone={onEditDone}
          onDelete={onDeleteEdit}
          selectedPoll={selectedPoll}
          selectedAbbrv={selectedAbbrv}
        />
      )}
      {showDeleteDialog && selectedPoll && (
        <PollDeleteModal
          open={showDeleteDialog}
          onClose={onCloseDelete}
          onDone={onDoneDelete}
          selectedPoll={selectedPoll}
        />
      )}
      {showCreatedDialog && selectedPoll && (
        <PollCreatedModal
          open={showCreatedDialog}
          onClose={onCloseCreated}
          selectedPoll={selectedPoll}
        />
      )}
    </>
  );
}
